import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Check, RotateCcw, Shuffle, Upload } from 'lucide-react';
import AdminShell from '../../components/AdminShell.jsx';
import { useFlash } from '../../context/ToastContext.jsx';
import AnimatedCounter from '../../components/AnimatedCounter.jsx';
import ChoicePicker from '../../components/ChoicePicker.jsx';
import ConfirmModal from '../../components/ConfirmModal.jsx';
import Loader from '../../components/Loader.jsx';
import { api } from '../../api/client.js';

// /admin/package-art — every image slot the app draws per package, plus the
// app-wide "global" slots, each one manually uploadable. Uploads live in R2
// and override the static files the repo ships; Reset deletes the override
// and the serve route falls back to the shipped default. No deploy, no
// grant re-apply. A package with tunable overlay screens (LAYOUT_SCREENS)
// also gets a live layout editor per screen on its tab.
//
// Slot inventory mirrors functions/_lib/packageArt.js — extend both together.

const SLOTS = [
  {
    kind: 'card',
    label: 'Card / key art',
    usedIn: 'Dashboard hero + tiles · Access page hero · console header underlay · overlay previews',
    hint: '16:9 landscape · ~1280×720+ · JPG / PNG / WebP',
    ratio: '16 / 9',
  },
  {
    kind: 'banner',
    label: 'Console banner',
    usedIn: 'Console header strip (layers over the card art)',
    hint: 'Very wide crop · ~1920×420+ · PNG / WebP, transparency OK',
    ratio: '21 / 6',
  },
];

// App-wide slots — uploaded under the reserved `global` scope.
const GLOBAL_SLOTS = [
  {
    kind: 'request-tile',
    label: 'Request a Game tile',
    usedIn: 'Dashboard — background of the Request a Game tile',
    hint: 'Roughly square-to-16:9 · JPG / PNG / WebP',
    ratio: '16 / 9',
  },
];

// The character-fit editors below are Tekken 8 tools — the roster comes from
// the winner-screen overlay and the previews load tekken-8 renders — so they
// ride along on that package's tab rather than standing on their own.
const FIT_PACKAGE_SLUG = 'tekken-8';

// Overlay screens with a live layout editor, per package. Mirrors LAYOUTS in
// functions/_lib/layouts.js — extend both together. 'all-screens' is the
// game's font, colours and vertical name, which every screen takes from
// Starting Soon's section (findEditor there).
const LAYOUT_SCREENS = {
  valorant: ['all-screens', 'starting-soon', 'up-next', 'bracket', 'in-match', 'commentator', 'champion', 'top-8'],
  'marvel-rivals': ['all-screens', 'starting-soon', 'in-match', 'commentator'],
};

const fmtSize = (n) => (n >= 1024 * 1024 ? `${(n / 1024 / 1024).toFixed(1)} MB` : `${Math.round(n / 1024)} KB`);

export default function PackageArt() {
  const [data, setData] = useState(null); // { packages, global }
  const setFlash = useFlash();
  const [busySlot, setBusySlot] = useState(null); // `${apiSlug}/${kind}`
  const [confirmReset, setConfirmReset] = useState(null); // { apiSlug, displayName, slot }
  const [tab, setTab] = useState(null); // selected package slug, or 'global'
  // Bumped after every mutation — cache-busts the previews past the serve
  // route's 5-minute Cache-Control.
  const [version, setVersion] = useState(() => Date.now());
  const fileInputs = useRef(new Map());

  function load() {
    return api.adminListPackageArt()
      .then((d) => setData({ packages: d?.packages ?? [], global: d?.global ?? {} }))
      .catch((e) => setFlash({ kind: 'danger', msg: e.message }));
  }

  useEffect(() => { load(); }, []);

  const packages = data?.packages;
  const customCount = useMemo(() => {
    const inPkgs = (packages ?? []).reduce(
      (n, p) => n + SLOTS.filter((s) => p.slots?.[s.kind]?.uploaded).length,
      0,
    );
    const inGlobal = GLOBAL_SLOTS.filter((s) => data?.global?.[s.kind]?.uploaded).length;
    return inPkgs + inGlobal;
  }, [packages, data]);
  const slotTotal = (packages?.length ?? 0) * SLOTS.length + GLOBAL_SLOTS.length;

  // One nav entry per game, then the app-wide group — the order the nav
  // shows them in. Version stays out of this so an upload refreshes the
  // thumbs without rebuilding the tab list.
  const navItems = useMemo(() => {
    if (data == null) return [];
    const games = (data.packages ?? []).map((p) => ({
      slug: p.slug,
      label: p.name,
      thumb: `/api/packages/${p.slug}/art/card`,
      custom: SLOTS.some((s) => p.slots?.[s.kind]?.uploaded),
    }));
    return [...games, {
      slug: 'global',
      label: 'Global',
      thumb: `/api/art/${GLOBAL_SLOTS[0].kind}`,
      custom: GLOBAL_SLOTS.some((s) => data.global?.[s.kind]?.uploaded),
    }];
  }, [data]);

  // Resolved rather than stored, so the tab survives a reload that renamed
  // or removed the package it pointed at.
  const active = navItems.some((i) => i.slug === tab) ? tab : navItems[0]?.slug;
  const activePkg = packages?.find((p) => p.slug === active);

  // The two fit editors tune Tekken 8 character renders — their previews
  // read /overlay/images/tekken-8/characters/ — so they belong on that
  // game's tab. Without a tekken-8 package they would be unreachable, so
  // they fall back to the always-present global tab.
  const fitHost = navItems.some((i) => i.slug === FIT_PACKAGE_SLUG) ? FIT_PACKAGE_SLUG : 'global';

  async function onPick(apiSlug, displayName, slot, ev) {
    const file = ev.target.files?.[0];
    ev.target.value = ''; // allow re-picking the same filename later
    if (!file) return;
    const id = `${apiSlug}/${slot.kind}`;
    setBusySlot(id);
    try {
      await api.adminUploadPackageArt(apiSlug, slot.kind, file);
      setFlash({ kind: 'success', msg: `${displayName} ${slot.label.toLowerCase()} updated (${file.name}, ${fmtSize(file.size)}).` });
      await load();
      setVersion(Date.now());
    } catch (e) {
      setFlash({ kind: 'danger', msg: `Upload failed: ${e.message}` });
    } finally {
      setBusySlot(null);
    }
  }

  async function doReset({ apiSlug, displayName, slot }) {
    const id = `${apiSlug}/${slot.kind}`;
    setBusySlot(id);
    setConfirmReset(null);
    try {
      await api.adminResetPackageArt(apiSlug, slot.kind);
      setFlash({ kind: 'success', msg: `${displayName} ${slot.label.toLowerCase()} reset to the shipped default.` });
      await load();
      setVersion(Date.now());
    } catch (e) {
      setFlash({ kind: 'danger', msg: `Reset failed: ${e.message}` });
    } finally {
      setBusySlot(null);
    }
  }

  function renderSlot({ apiSlug, displayName, slot, meta, serveUrl }) {
    const id = `${apiSlug}/${slot.kind}`;
    const busy = busySlot === id;
    return (
      <div className="pa-slot" key={id}>
        <div className="pa-slot__head">
          <span className="pa-slot__label">{slot.label}</span>
          {meta.uploaded
            ? <span className="adm-chip adm-chip--blue">Custom</span>
            : <span className="adm-chip adm-chip--wait">Default</span>}
        </div>
        <ArtPreview
          src={`${serveUrl}?v=${version}`}
          ratio={slot.ratio}
          alt={`${displayName} ${slot.label}`}
        />
        <div className="pa-slot__meta">
          {busy ? 'Working…' : meta.uploaded ? (
            <>
              {meta.filename || 'uploaded file'}
              {meta.size != null && <> · {fmtSize(meta.size)}</>}
              {meta.uploadedAt && <> · {new Date(meta.uploadedAt).toLocaleDateString()}</>}
              {meta.uploadedBy && <> · by {meta.uploadedBy}</>}
            </>
          ) : 'Shipped with the app'}
        </div>
        <div className="pa-slot__where">{slot.usedIn}</div>
        <div className="pa-slot__actions">
          <input
            ref={(el) => {
              if (el) fileInputs.current.set(id, el);
              else fileInputs.current.delete(id);
            }}
            type="file"
            className="d-none"
            accept="image/png,image/jpeg,image/webp,image/gif"
            onChange={(ev) => onPick(apiSlug, displayName, slot, ev)}
          />
          <button
            type="button"
            className="adm-act"
            disabled={busy}
            onClick={() => fileInputs.current.get(id)?.click()}
            title={`${slot.hint} · max 12MB`}
          >
            <Upload size={13} aria-hidden="true" />
            {meta.uploaded ? 'Replace' : 'Upload'}
          </button>
          {meta.uploaded && (
            <button
              type="button"
              className="btn-icon btn-icon--warning"
              disabled={busy}
              onClick={() => setConfirmReset({ apiSlug, displayName, slot })}
              title="Remove the upload and return to the shipped default"
              aria-label="Reset to default"
            >
              <RotateCcw size={16} aria-hidden="true" />
            </button>
          )}
          <span className="pa-slot__hint">{slot.hint}</span>
        </div>
      </div>
    );
  }

  return (
    <AdminShell crumb="Package Art">
      <header className="adm-head adm-head--split adm-rv" style={{ animationDelay: '80ms' }}>
        <div>
          <h1 className="adm-head__title">Package Art</h1>
          <p className="adm-head__sub">
            Every image the app draws per package — plus the app-wide slots — uploadable in
            place. Uploads override the shipped files immediately, no deploy; Reset returns
            a slot to the default.
          </p>
        </div>
        <div className="adm-stats">
          <div className="adm-stat">
            <div className="adm-stat__value"><AnimatedCounter to={packages?.length ?? 0} pad={2} duration={1200} /></div>
            <div className="adm-stat__label">Packages</div>
          </div>
          <span className="adm-stats__sep" aria-hidden="true" />
          <div className="adm-stat adm-stat--blue">
            <div className="adm-stat__value"><AnimatedCounter to={customCount} pad={2} duration={1200} /></div>
            <div className="adm-stat__label">Custom</div>
          </div>
          <span className="adm-stats__sep" aria-hidden="true" />
          <div className="adm-stat adm-stat--ok">
            <div className="adm-stat__value"><AnimatedCounter to={slotTotal - customCount} pad={2} duration={1200} /></div>
            <div className="adm-stat__label">Default</div>
          </div>
        </div>
      </header>


      {data == null ? (
        <Loader label="Loading packages…" />
      ) : (
        <>
          <GameNav items={navItems} active={active} onSelect={setTab} version={version} />
          <div className="adm-grid__rule adm-rv" aria-hidden="true" style={{ animationDelay: '160ms' }} />

          {activePkg && (
            <section
              key={activePkg.slug}
              className="adm-rvl"
              role="tabpanel"
              aria-labelledby={`pa-tab-${activePkg.slug}`}
              style={{ animationDelay: '120ms' }}
            >
              <div className="adm-group__head">
                <span className="adm-initial" aria-hidden="true">{activePkg.name?.[0]?.toUpperCase() ?? '?'}</span>
                <span className="adm-group__name">{activePkg.name}</span>
                <span className="adm-group__summary">{activePkg.slug}</span>
              </div>
              <div className="pa-grid">
                {SLOTS.map((slot) => renderSlot({
                  apiSlug: activePkg.slug,
                  displayName: activePkg.name,
                  slot,
                  meta: activePkg.slots?.[slot.kind] ?? { uploaded: false },
                  serveUrl: `/api/packages/${activePkg.slug}/art/${slot.kind}`,
                }))}
              </div>
            </section>
          )}

          {active === 'global' && (
            <section
              key="global"
              className="adm-rvl"
              role="tabpanel"
              aria-labelledby="pa-tab-global"
              style={{ animationDelay: '120ms' }}
            >
              <div className="adm-group__head">
                <span className="adm-initial adm-initial--pink" aria-hidden="true">G</span>
                <span className="adm-group__name">Global</span>
                <span className="adm-group__summary">shared across the app, not tied to a package</span>
              </div>
              <div className="pa-grid">
                {GLOBAL_SLOTS.map((slot) => renderSlot({
                  apiSlug: 'global',
                  displayName: 'Global',
                  slot,
                  meta: data.global?.[slot.kind] ?? { uploaded: false },
                  serveUrl: `/api/art/${slot.kind}`,
                }))}
              </div>
            </section>
          )}

          {packages.length === 0 && (
            <div className="adm-empty">No packages in the system yet.</div>
          )}

          {/* Hidden rather than unmounted: both editors hold a whole unsaved
              fit map, and switching tabs must not throw it away. */}
          <div hidden={active !== fitHost}>
            <WinnerCharFit onFlash={setFlash} />
            <Top8CharFit onFlash={setFlash} />
          </div>

          {/* Same for the layout editors and their unsaved drafts. */}
          {packages.filter((p) => LAYOUT_SCREENS[p.slug]).map((p) => (
            <div key={`layouts-${p.slug}`} hidden={active !== p.slug}>
              <ScreenLayouts pkg={p.slug} screens={LAYOUT_SCREENS[p.slug]} onFlash={setFlash} />
            </div>
          ))}
        </>
      )}

      <ConfirmModal
        open={confirmReset != null}
        title={`Reset ${confirmReset?.slot.label.toLowerCase()}?`}
        body={confirmReset && (
          <>
            <p className="mb-2">
              Removes the uploaded {confirmReset.slot.label.toLowerCase()} for{' '}
              <strong>{confirmReset.displayName}</strong> — the app goes back to the
              file shipped with the deploy.
            </p>
            <p className="text-muted small mb-0">The uploaded file is deleted; keep a copy if you may want it back.</p>
          </>
        )}
        confirmLabel="Reset"
        confirmKind="danger"
        onConfirm={() => confirmReset && doReset(confirmReset)}
        onClose={() => setConfirmReset(null)}
      />
    </AdminShell>
  );
}

// ── Game nav ─────────────────────────────────────────────────────────────
// The page is a partial view: one game at a time. Each entry owns a single
// package's art, so picking one swaps the panel below instead of scrolling a
// stack of every game at once. `Global` is the same shape for the app-wide
// slots. The dot flags a game that has at least one uploaded override, so
// the tabs still say where the customs are without visiting each one.

function GameNav({ items, active, onSelect, version }) {
  if (items.length < 2) return null;

  return (
    <nav className="pa-nav" role="tablist" aria-label="Games and global art">
      {items.map((it) => (
        <button
          key={it.slug}
          id={`pa-tab-${it.slug}`}
          type="button"
          role="tab"
          aria-selected={active === it.slug}
          className={`pa-nav__link${active === it.slug ? ' is-active' : ''}`}
          onClick={() => onSelect(it.slug)}
        >
          <NavThumb src={`${it.thumb}?v=${version}`} label={it.label} />
          <span className="pa-nav__name">{it.label}</span>
          {it.custom && (
            <span className="pa-nav__dot" title="Has custom art" aria-hidden="true" />
          )}
        </button>
      ))}
    </nav>
  );
}

// Same 302-to-a-404 hazard as ArtPreview: a package with no shipped key art
// gets a broken glyph, so fall back to the group head's initial instead.
function NavThumb({ src, label }) {
  const [failed, setFailed] = useState(false);
  useEffect(() => { setFailed(false); }, [src]);
  if (failed) {
    return (
      <span className="pa-nav__thumb pa-nav__thumb--empty" aria-hidden="true">
        {label?.[0]?.toUpperCase() ?? '?'}
      </span>
    );
  }
  return <img className="pa-nav__thumb" src={src} alt="" onError={() => setFailed(true)} />;
}

// ── Character fit preview ────────────────────────────────────────────────
// Both fit editors preview on the real overlay page in ?preview=1 mode, like
// the layout editors: the page shows sample players, takes the draft fit map
// and the characters to show by postMessage, and never polls. What it shows
// is what airs, art fallbacks and crop included.

function useFitPreview(message) {
  const frame = useRef(null);
  const post = useCallback(() => {
    frame.current?.contentWindow?.postMessage({ type: 'streamgg:char-fit', ...message }, window.location.origin);
  }, [message]);
  useEffect(() => { post(); }, [post]);
  return [frame, post];
}

// ── Winner-overlay character fit ─────────────────────────────────────────
// Every character render has its own pose, so the winner screen's crop
// window is tunable per character: X shift, Y offset, scale. The map lives
// in R2 (art/global/winner-char-fit.json); the overlay refetches it every
// minute, so a save here reaches OBS without touching sources.

const FIT_DEFAULTS = { x: 0, y: 2, scale: 152 };
const FIT_FIELDS = [
  { key: 'x', label: 'X shift', min: -100, max: 100, step: 0.5 },
  { key: 'y', label: 'Y offset', min: -100, max: 100, step: 0.5 },
  { key: 'scale', label: 'Scale', min: 60, max: 300, step: 1 },
];

function WinnerCharFit({ onFlash }) {
  const [data, setData] = useState(null); // { characters, map }
  const [map, setMap] = useState({});
  const [sel, setSel] = useState('');
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const preview = useMemo(() => ({ map, character: sel }), [map, sel]);
  const [frame, post] = useFitPreview(preview);

  useEffect(() => {
    api.adminGetWinnerCharFit()
      .then((d) => {
        setData({ characters: d?.characters ?? [] });
        setMap(d?.map ?? {});
        setSel((d?.characters ?? [])[0]?.value ?? '');
      })
      .catch((e) => onFlash({ kind: 'danger', msg: e.message }));
  }, [onFlash]);

  if (data == null) return null;

  const fit = { ...FIT_DEFAULTS, ...(map[sel] ?? {}) };
  const tuned = Object.keys(map);
  const isTuned = sel in map;

  function setField(key, value) {
    const n = Number(value);
    if (!Number.isFinite(n)) return;
    setMap((prev) => ({ ...prev, [sel]: { ...FIT_DEFAULTS, ...(prev[sel] ?? {}), [key]: n } }));
    setDirty(true);
  }

  function clearSel() {
    setMap((prev) => {
      const next = { ...prev };
      delete next[sel];
      return next;
    });
    setDirty(true);
  }

  async function save() {
    setSaving(true);
    try {
      const res = await api.adminPutWinnerCharFit(map);
      setMap(res?.map ?? map);
      setDirty(false);
      onFlash({ kind: 'success', msg: 'Character fit saved — live overlays pick it up within a minute.' });
    } catch (e) {
      onFlash({ kind: 'danger', msg: `Save failed: ${e.message}` });
    } finally {
      setSaving(false);
    }
  }

  return (
    <section className="adm-rvl" style={{ animationDelay: '400ms' }} aria-label="Winner character fit">
      <div className="adm-group__head">
        <span className="adm-initial adm-initial--amber" aria-hidden="true">W</span>
        <span className="adm-group__name">Winner character fit</span>
        <span className="adm-group__summary">
          per-render position in the winner screen&apos;s window · {tuned.length} tuned
        </span>
      </div>
      <div className="pa-fit">
        <div className="pa-fit__preview-col pa-fit__preview-col--wide">
          <div className="pa-layout__screen">
            <iframe
              ref={frame}
              src="/overlay/winner_screen.html?preview=1"
              title="Winner screen preview"
              tabIndex={-1}
              onLoad={post}
            />
          </div>
          <div className="pa-slot__where">
            The real winner screen with a sample player, showing your changes as you make them.
            Saving sends them to every winner screen on air.
          </div>
        </div>
        <div className="pa-fit__controls">
          <label className="pa-fit__row">
            <span className="pa-fit__label">Character</span>
            <select
              className="pa-fit__select"
              value={sel}
              onChange={(e) => setSel(e.target.value)}
            >
              {data.characters.map((c) => (
                <option key={c.value} value={c.value}>
                  {c.label}{c.value in map ? ' •' : ''}
                </option>
              ))}
            </select>
          </label>
          {FIT_FIELDS.map((f) => (
            <label className="pa-fit__row" key={f.key}>
              <span className="pa-fit__label">{f.label}</span>
              <input
                type="range"
                min={f.min}
                max={f.max}
                step={f.step}
                value={fit[f.key]}
                onChange={(e) => setField(f.key, e.target.value)}
              />
              <input
                className="pa-fit__num"
                type="number"
                min={f.min}
                max={f.max}
                step={f.step}
                value={fit[f.key]}
                onChange={(e) => setField(f.key, e.target.value)}
              />
            </label>
          ))}
          <div className="pa-fit__actions">
            <button type="button" className="adm-act adm-act--green" disabled={saving || !dirty} onClick={save}>
              <Check size={13} aria-hidden="true" />
              {saving ? 'Saving…' : 'Save fit map'}
            </button>
            {isTuned && (
              <button type="button" className="adm-act adm-act--quiet" disabled={saving} onClick={clearSel}>
                Reset this character
              </button>
            )}
            <span className="pa-slot__hint">
              {dirty ? 'Unsaved changes' : 'Saved · overlays refresh within ~1 min'}
            </span>
          </div>
        </div>
      </div>
    </section>
  );
}

// ── Top 8 (landscape) character fit ──────────────────────────────────────
// Same idea as the winner fit, but each of the THREE card windows tunes
// independently: the stored shape is { slug: { champ|rt|rb: {x,y,scale} } }
// and the overlay applies the matching window's entry via --fit-* CSS vars —
// identity defaults (x 0, y 0, scale 100) render exactly like the untuned
// CSS, per window. The preview puts the character being tuned in 1st, 2nd
// and 5th — one card of each window — and random others in the other five
// places, each at its own draft fit, so the whole screen reads as it would
// on air. Click a card in the preview (or use the selector) to pick which
// window the sliders edit.

const FIT8_DEFAULTS = { x: 0, y: 0, scale: 100 };
const FIT8_FIELDS = [
  { key: 'x', label: 'X shift', min: -100, max: 100, step: 0.5 },
  { key: 'y', label: 'Y lift', min: -100, max: 100, step: 0.5 },
  { key: 'scale', label: 'Scale', min: 40, max: 300, step: 1 },
];
const FIT8_WINDOWS = [
  { key: 'champ', tag: 'Champion' },
  { key: 'rt', tag: '2nd–4th' },
  { key: 'rb', tag: '5th–7th' },
];
// Standings order of the preview: the tuned character in 1st, 2nd and 5th
// (null), the random others filling the rest in turn.
const FIT8_PREVIEW_ORDER = [null, null, 0, 1, null, 2, 3, 4];

// `n` random roster slugs, none of them `sel` or one already in `keep`.
function pickOthers(characters, sel, n, keep = []) {
  const pool = characters.map((c) => c.value).filter((v) => v !== sel && !keep.includes(v));
  for (let i = pool.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    [pool[i], pool[j]] = [pool[j], pool[i]];
  }
  return pool.slice(0, n);
}

function Top8CharFit({ onFlash }) {
  const [data, setData] = useState(null); // { characters }
  const [map, setMap] = useState({});
  const [sel, setSel] = useState('');
  const [others, setOthers] = useState([]); // the preview's random other places
  const [win, setWin] = useState('champ');
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);

  // The overlay derives each art slug from the start.gg character name, so
  // the preview sends names, as start.gg would.
  const preview = useMemo(() => {
    const label = (slug) => data?.characters.find((c) => c.value === slug)?.label ?? slug;
    const characters = sel ? FIT8_PREVIEW_ORDER.map((i) => label(i == null ? sel : others[i])) : [];
    return { map, characters };
  }, [data, map, sel, others]);
  const [frame, post] = useFitPreview(preview);

  useEffect(() => {
    api.adminGetTop8CharFit()
      .then((d) => {
        const characters = d?.characters ?? [];
        const first = characters[0]?.value ?? '';
        setData({ characters });
        setMap(d?.map ?? {});
        setSel(first);
        setOthers(pickOthers(characters, first, 5));
      })
      .catch((e) => onFlash({ kind: 'danger', msg: e.message }));
  }, [onFlash]);

  useEffect(() => {
    function onMessage(e) {
      if (e.origin !== window.location.origin || e.source !== frame.current?.contentWindow) return;
      if (e.data?.type !== 'streamgg:char-fit-window') return;
      if (FIT8_WINDOWS.some((w) => w.key === e.data.window)) setWin(e.data.window);
    }
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, [frame]);

  if (data == null) return null;

  const winFits = map[sel] ?? {};
  const fit = { ...FIT8_DEFAULTS, ...(winFits[win] ?? {}) };
  const tuned = Object.keys(map);
  const isTuned = sel in map;
  const isTunedWin = win in winFits;

  function setField(key, value) {
    const n = Number(value);
    if (!Number.isFinite(n)) return;
    setMap((prev) => ({
      ...prev,
      [sel]: {
        ...(prev[sel] ?? {}),
        [win]: { ...FIT8_DEFAULTS, ...((prev[sel] ?? {})[win] ?? {}), [key]: n },
      },
    }));
    setDirty(true);
  }

  function selectCharacter(value) {
    setSel(value);
    // Never show the tuned character in one of the random places too.
    setOthers((prev) => (prev.includes(value)
      ? prev.map((v) => (v === value ? pickOthers(data.characters, value, 1, prev)[0] ?? v : v))
      : prev));
  }

  function clearWin() {
    setMap((prev) => {
      const entry = { ...(prev[sel] ?? {}) };
      delete entry[win];
      const next = { ...prev };
      if (Object.keys(entry).length) next[sel] = entry; else delete next[sel];
      return next;
    });
    setDirty(true);
  }

  function clearSel() {
    setMap((prev) => {
      const next = { ...prev };
      delete next[sel];
      return next;
    });
    setDirty(true);
  }

  async function save() {
    setSaving(true);
    try {
      const res = await api.adminPutTop8CharFit(map);
      setMap(res?.map ?? map);
      setDirty(false);
      onFlash({ kind: 'success', msg: 'Top 8 fit saved — live overlays pick it up within a minute.' });
    } catch (e) {
      onFlash({ kind: 'danger', msg: `Save failed: ${e.message}` });
    } finally {
      setSaving(false);
    }
  }

  return (
    <section className="adm-rvl" style={{ animationDelay: '500ms' }} aria-label="Top 8 character fit">
      <div className="adm-group__head">
        <span className="adm-initial adm-initial--pink" aria-hidden="true">8</span>
        <span className="adm-group__name">Top 8 character fit</span>
        <span className="adm-group__summary">
          per-render position, each card window tuned on its own · {tuned.length} tuned
        </span>
      </div>
      <div className="pa-fit">
        <div className="pa-fit__preview-col pa-fit__preview-col--wide">
          <div className="pa-layout__screen pa-layout__screen--click">
            <iframe
              ref={frame}
              src="/overlay/top_8.html?preview=1"
              title="Top 8 preview"
              tabIndex={-1}
              onLoad={post}
            />
          </div>
          <div className="pa-slot__where">
            The real Top 8 with sample players: the character you tune is 1st, 2nd and 5th, one
            card of each window, and the other places are random. Click a card to tune its window.
          </div>
        </div>
        <div className="pa-fit__controls">
          <label className="pa-fit__row">
            <span className="pa-fit__label">Character</span>
            <select
              className="pa-fit__select"
              value={sel}
              onChange={(e) => selectCharacter(e.target.value)}
            >
              {data.characters.map((c) => (
                <option key={c.value} value={c.value}>
                  {c.label}{c.value in map ? ' •' : ''}
                </option>
              ))}
            </select>
          </label>
          <div className="pa-fit__row">
            <span className="pa-fit__label">Others</span>
            <button
              type="button"
              className="adm-act adm-act--quiet"
              onClick={() => setOthers(pickOthers(data.characters, sel, 5))}
              title="New random characters for 3rd, 4th, the second 5th and both 7ths"
            >
              <Shuffle size={13} aria-hidden="true" />
              Shuffle
            </button>
          </div>
          <div className="pa-fit__row">
            <span className="pa-fit__label">Window</span>
            <div className="pa-t8__winsel" role="tablist">
              {FIT8_WINDOWS.map((w) => (
                <button
                  key={w.key}
                  type="button"
                  role="tab"
                  aria-selected={win === w.key}
                  className={`pa-t8__winbtn${win === w.key ? ' is-active' : ''}`}
                  onClick={() => setWin(w.key)}
                >
                  {w.tag}{w.key in winFits ? ' •' : ''}
                </button>
              ))}
            </div>
          </div>
          {FIT8_FIELDS.map((f) => (
            <label className="pa-fit__row" key={f.key}>
              <span className="pa-fit__label">{f.label}</span>
              <input
                type="range"
                min={f.min}
                max={f.max}
                step={f.step}
                value={fit[f.key]}
                onChange={(e) => setField(f.key, e.target.value)}
              />
              <input
                className="pa-fit__num"
                type="number"
                min={f.min}
                max={f.max}
                step={f.step}
                value={fit[f.key]}
                onChange={(e) => setField(f.key, e.target.value)}
              />
            </label>
          ))}
          <div className="pa-fit__actions">
            <button type="button" className="adm-act adm-act--green" disabled={saving || !dirty} onClick={save}>
              <Check size={13} aria-hidden="true" />
              {saving ? 'Saving…' : 'Save fit map'}
            </button>
            {isTunedWin && (
              <button type="button" className="adm-act adm-act--quiet" disabled={saving} onClick={clearWin}>
                Reset this window
              </button>
            )}
            {isTuned && (
              <button type="button" className="adm-act adm-act--quiet" disabled={saving} onClick={clearSel}>
                Reset this character
              </button>
            )}
            <span className="pa-slot__hint">
              {dirty ? 'Unsaved changes' : 'Saved · overlays refresh within ~1 min'}
            </span>
          </div>
        </div>
      </div>
    </section>
  );
}

// ── Screen layout ────────────────────────────────────────────────────────
// A package screen's live-tunable values (functions/_lib/layouts.js): its
// colours, the agent's place and size, the title and the timer, and how its
// sponsors are shown (one group at a time, the organizer always on, or every
// group side by side) and spaced. Every field starts at the overlay's design;
// only changes are stored (R2), and live overlays pick a save up within ~15s,
// no HTML re-upload. The preview is the real overlay page in ?preview=1 mode
// with sample content, sent the draft by postMessage as it changes — what it
// shows is what airs.
//
// A field with `showIf` is greyed out while the draft doesn't meet it (the
// organizer's side and the space between groups mean nothing when one group
// shows at a time). It stays in sight, with its reset, because its value stays
// in the draft and is saved with the rest: switching modes back and forth
// loses nothing, and a value kept for another mode is never out of view.
//
// A package's screens are tabs (ScreenLayouts), and so are a screen's groups
// of fields: with every group of every screen in one column, the preview
// scrolled out of sight while the fields were set (the client's complaint).
// One group shows at a time, and from a wide window up the preview stays put
// as the page scrolls.
//
// A game's All screens tab holds what every one of its screens shares (the
// font, the colours, the name down the edge), stored with Starting Soon's
// values; its preview switches between the game's screens. Each preview is
// drawn over the saved values the tab doesn't edit (`previews[].vars`), so
// Starting Soon's tab shows the saved font and the All screens tab each
// screen's saved layout; a save on one tab re-reads them on the others.

// The tabs pattern's keys on a tablist: arrows move to the next or previous
// tab (round the ends), Home and End to the first and last, selecting it and
// taking the focus along. The tabs carry data-tab; only the selected one is
// in the Tab order.
function onTabKeys(e, keys, current, select) {
  const i = keys.indexOf(current);
  const to = { ArrowRight: i + 1, ArrowLeft: i - 1, Home: 0, End: keys.length - 1 }[e.key];
  if (to === undefined) return;
  e.preventDefault();
  const next = keys[(to + keys.length) % keys.length];
  select(next);
  const list = e.currentTarget;
  requestAnimationFrame(() => {
    list.querySelector(`[data-tab="${CSS.escape(next)}"]`)?.focus();
  });
}

// The package's tunable screens, one at a time. The editors stay mounted
// (hidden) so a screen's unsaved draft survives a change of tab; a tab
// carries a dot while its screen has one. The reveal animation is on this
// wrapper, not on each editor: an editor being shown again would replay it.
function ScreenLayouts({ pkg, screens, onFlash }) {
  const [active, setActive] = useState(screens[0]);
  const [states, setStates] = useState({}); // screen → { label, dirty }
  const [saves, setSaves] = useState(0); // bumped by every save
  const onState = useCallback((screen, s) => setStates((prev) => (
    prev[screen]?.label === s.label && prev[screen]?.dirty === s.dirty ? prev : { ...prev, [screen]: s }
  )), []);
  const onSaved = useCallback(() => setSaves((n) => n + 1), []);

  return (
    <div className="pa-layouts adm-rvl" style={{ animationDelay: '200ms' }}>
      <div
        className="pa-layout__tabs pa-layout__tabs--screens"
        role="tablist"
        aria-label="Screens"
        onKeyDown={(e) => onTabKeys(e, screens, active, setActive)}
      >
        {screens.map((s) => (
          <button
            key={s}
            id={`pa-screen-tab-${pkg}-${s}`}
            data-tab={s}
            type="button"
            role="tab"
            tabIndex={active === s ? 0 : -1}
            aria-selected={active === s}
            aria-controls={`pa-screen-${pkg}-${s}`}
            className={`pa-layout__tab${active === s ? ' is-active' : ''}`}
            onClick={() => setActive(s)}
          >
            {states[s]?.label ?? s.replace(/-/g, ' ')}
            {states[s]?.dirty && (
              <>
                <span className="pa-layout__tab-dot" title="Unsaved changes" aria-hidden="true" />
                <span className="visually-hidden">, unsaved changes</span>
              </>
            )}
          </button>
        ))}
      </div>
      {screens.map((s) => (
        <div
          key={s}
          id={`pa-screen-${pkg}-${s}`}
          role="tabpanel"
          aria-labelledby={`pa-screen-tab-${pkg}-${s}`}
          hidden={active !== s}
        >
          <LayoutEditor pkg={pkg} screen={s} saves={saves} onSaved={onSaved} onFlash={onFlash} onState={onState} />
        </div>
      ))}
    </div>
  );
}

const fillLayout = (fields, v) => Object.fromEntries(fields.map((f) => [f.key, v?.[f.key] ?? f.default]));
const layoutOverrides = (fields, v) =>
  Object.fromEntries(fields.filter((f) => v[f.key] !== f.default).map((f) => [f.key, v[f.key]]));
// Mirrors layoutVars() in functions/_lib/layouts.js. `every` sends the design
// values too: the All screens values are ones a preview page also fetches as
// saved, and a draft put back to the design has to win over them.
const layoutCss = (fields, v, every = false) => Object.fromEntries(fields.filter((f) => every || v[f.key] !== f.default).map((f) => [
  f.css,
  f.type === 'color' || f.type === 'choice'
    ? v[f.key]
    : `${Number((v[f.key] * (f.cssScale ?? 1)).toFixed(4))}${f.cssUnit ?? ''}`,
]));
const fieldApplies = (f, v) =>
  !f.showIf || Object.entries(f.showIf).every(([key, allowed]) => allowed.includes(v[key]));
// "Used when Show is Organizer always on or All side by side", from showIf.
const whenApplies = (f, fields) => Object.entries(f.showIf ?? {}).map(([key, allowed]) => {
  const other = fields.find((x) => x.key === key);
  const labels = allowed.map((a) => other?.options?.find((o) => o.value === a)?.label ?? a);
  return `Used when ${other?.label ?? key} is ${labels.join(' or ')}`;
}).join('; ');
const designValue = (f) => {
  if (f.type === 'color') return f.default.toUpperCase();
  if (f.type === 'choice') return f.options.find((o) => o.value === f.default)?.label ?? f.default;
  return `${f.default}${f.unit}`;
};

function LayoutEditor({ pkg, screen, saves, onSaved, onFlash, onState }) {
  const [def, setDef] = useState(null); // { label, page, allScreens, fields }
  const [values, setValues] = useState({}); // the draft, every field filled in
  const [saved, setSaved] = useState({}); // the stored overrides
  const [previews, setPreviews] = useState([]); // [{ label, page, vars }]
  const [previewAt, setPreviewAt] = useState(0);
  const [saving, setSaving] = useState(false);
  const [tab, setTab] = useState(null); // the group shown; null = the first
  const [failed, setFailed] = useState(null); // why the layout couldn't load
  const frame = useRef(null);

  useEffect(() => {
    api.adminGetLayout(pkg, screen)
      .then((d) => {
        setDef(d);
        setSaved(d?.values ?? {});
        setValues(fillLayout(d?.fields ?? [], d?.values));
        setPreviews(d?.previews ?? []);
      })
      .catch((e) => {
        setFailed(e.message);
        onFlash({ kind: 'danger', msg: `${pkg} ${screen} layout: ${e.message}` });
      });
  }, [pkg, screen, onFlash]);

  // A save on another tab can change what this preview is drawn over; the
  // draft stays as it is.
  useEffect(() => {
    if (!saves) return;
    api.adminGetLayout(pkg, screen).then((d) => setPreviews(d?.previews ?? []), () => {});
  }, [saves, pkg, screen]);

  const fields = def?.fields ?? [];
  const preview = previews[previewAt] ?? previews[0];
  const vars = useMemo(
    () => ({ ...preview?.vars, ...layoutCss(fields, values, def?.allScreens) }),
    [preview, fields, values, def],
  );
  const post = useCallback(() => {
    frame.current?.contentWindow?.postMessage({ type: 'streamgg:layout', vars }, window.location.origin);
  }, [vars]);
  useEffect(() => { post(); }, [post]);

  const overrides = layoutOverrides(fields, values);
  const dirty = JSON.stringify(overrides) !== JSON.stringify(saved);
  useEffect(() => {
    if (def) onState?.(screen, { label: def.label, dirty });
  }, [def, dirty, screen, onState]);

  // A group of fields for one screen (`previewScreen`: Starting Soon's
  // Thank You fields) turns the preview to that screen when it opens.
  const shownGroup = tab ?? fields[0]?.group;
  const groupScreen = fields.find((f) => f.group === shownGroup && f.previewScreen)?.previewScreen;
  // A group whose fields only show in one layout of a screen (`previewQuery`:
  // the commentator page's nameplates, on Camera only) opens the preview in it.
  const groupQuery = fields.find((f) => f.group === shownGroup && f.previewQuery)?.previewQuery;
  useEffect(() => {
    if (!groupScreen) return;
    const i = previews.findIndex((p) => p.label === groupScreen);
    if (i >= 0) setPreviewAt(i);
  }, [groupScreen, previews.length]);
  // A picture dropdown's pictures are drawn in the display font the screens
  // use, which is saved on All screens and drawn into the preview.
  const font = preview?.vars?.['--font-display'] || 'anton';

  if (failed) {
    return (
      <div className="adm-empty">
        This screen&apos;s layout didn&apos;t load ({failed}). Reload the page to try again.
      </div>
    );
  }
  if (def == null) return null;

  const groups = [];
  for (const f of fields) {
    const g = groups.find((x) => x.name === f.group);
    if (g) g.fields.push(f); else groups.push({ name: f.group, fields: [f] });
  }
  const shown = groups.find((g) => g.name === tab) ?? groups[0];
  const atDesign = Object.keys(overrides).length === 0;

  function setField(f, raw) {
    let v = raw;
    if (f.type === 'number') {
      v = Number(raw);
      if (!Number.isFinite(v)) return;
      v = Math.min(f.max, Math.max(f.min, v));
    } else if (f.type === 'choice') {
      if (!f.options.some((o) => o.value === v)) return;
    } else if (!/^#[0-9a-f]{6}$/i.test(v)) {
      return;
    }
    setValues((prev) => ({ ...prev, [f.key]: f.type === 'color' ? v.toLowerCase() : v }));
  }

  async function save() {
    setSaving(true);
    try {
      const res = await api.adminPutLayout(pkg, screen, overrides);
      setSaved(res?.values ?? overrides);
      setValues(fillLayout(fields, res?.values ?? overrides));
      onSaved?.();
      onFlash({ kind: 'success', msg: `${def.label} layout saved — live overlays pick it up within ~15 seconds.` });
    } catch (e) {
      onFlash({ kind: 'danger', msg: `Save failed: ${e.message}` });
    } finally {
      setSaving(false);
    }
  }

  return (
    <section aria-label={`${def.label} layout`}>
      <div className="adm-group__head">
        <span className="adm-initial adm-initial--pink" aria-hidden="true">L</span>
        <span className="adm-group__name">{def.label} layout</span>
        <span className="adm-group__summary">
          live on air, no re-upload · {Object.keys(saved).length ? `${Object.keys(saved).length} changed from the design` : 'the design'}
        </span>
      </div>
      <div className="pa-fit">
        <div className="pa-fit__preview-col pa-layout__preview-col">
          <div className="pa-layout__screen">
            <iframe
              ref={frame}
              src={`${preview?.page ?? def.page}?preview=1${groupQuery ? `&${groupQuery}` : ''}`}
              title={`${preview?.label ?? def.label} overlay preview`}
              tabIndex={-1}
              onLoad={post}
            />
          </div>
          {previews.length > 1 && (
            <div className="pa-layout__choice pa-layout__previews" role="group" aria-label="Screen in the preview">
              {previews.map((p, i) => {
                const on = p === preview;
                return (
                  <button
                    key={p.page}
                    type="button"
                    className={`pa-layout__choice-btn${on ? ' is-active' : ''}`}
                    aria-pressed={on}
                    onClick={() => setPreviewAt(i)}
                  >
                    {p.label}
                  </button>
                );
              })}
            </div>
          )}
          <div className="pa-slot__where">
            {def.allScreens
              ? 'The real overlays with sample content, each with its own saved layout, showing your changes as you make them. Saving sends them to every screen of this game on air.'
              : 'The real overlay with sample content, showing your changes as you make them. Saving sends them to every overlay of this package on air.'}
          </div>
        </div>
        <div className="pa-fit__controls">
          <div
            className="pa-layout__tabs"
            role="tablist"
            aria-label={`${def.label} settings`}
            onKeyDown={(e) => onTabKeys(e, groups.map((g) => g.name), shown.name, setTab)}
          >
            {groups.map((g, i) => (
              <button
                key={g.name}
                id={`pa-group-tab-${pkg}-${screen}-${i}`}
                data-tab={g.name}
                type="button"
                role="tab"
                tabIndex={g === shown ? 0 : -1}
                aria-selected={g === shown}
                aria-controls={`pa-group-${pkg}-${screen}`}
                className={`pa-layout__tab${g === shown ? ' is-active' : ''}`}
                onClick={() => setTab(g.name)}
              >
                {g.name}
                {g.fields.some((f) => values[f.key] !== f.default) && (
                  <>
                    <span className="pa-layout__tab-dot pa-layout__tab-dot--changed" title="Changed from the design" aria-hidden="true" />
                    <span className="visually-hidden">, changed from the design</span>
                  </>
                )}
              </button>
            ))}
          </div>
          {[shown].map((g) => (
            <div
              className="pa-layout__group"
              key={g.name}
              id={`pa-group-${pkg}-${screen}`}
              role="tabpanel"
              aria-labelledby={`pa-group-tab-${pkg}-${screen}-${groups.indexOf(g)}`}
            >
              {g.fields.map((f) => {
                const changed = values[f.key] !== f.default;
                const idle = !fieldApplies(f, values);
                return (
                  <Fragment key={f.key}>
                    <div
                      className={`pa-fit__row${idle ? ' is-idle' : ''}`}
                      title={idle ? whenApplies(f, fields) : undefined}
                    >
                      <span className="pa-fit__label">{f.label}</span>
                      {f.type === 'color' ? (
                        <>
                          <input
                            className="pa-layout__color"
                            type="color"
                            value={values[f.key]}
                            disabled={idle}
                            onChange={(e) => setField(f, e.target.value)}
                            aria-label={`${g.name} ${f.label}`}
                          />
                          <span className="pa-layout__hex">{values[f.key].toUpperCase()}</span>
                        </>
                      ) : f.type === 'choice' && f.picker ? (
                        <ChoicePicker
                          id={`pa-${pkg}-${screen}-${f.key}`}
                          label={`${g.name} ${f.label}`}
                          value={values[f.key]}
                          options={f.options}
                          disabled={idle}
                          imageOf={(o) => o.image?.replace('{font}', font)}
                          onChange={(v) => setField(f, v)}
                        />
                      ) : f.type === 'choice' ? (
                        <div className="pa-layout__choice" role="group" aria-label={`${g.name} ${f.label}`}>
                          {f.options.map((o) => {
                            const on = values[f.key] === o.value;
                            return (
                              <button
                                key={o.value}
                                type="button"
                                className={`pa-layout__choice-btn${on ? ' is-active' : ''}`}
                                aria-pressed={on}
                                disabled={idle}
                                onClick={() => setField(f, o.value)}
                              >
                                {o.label}
                              </button>
                            );
                          })}
                        </div>
                      ) : (
                        <>
                          <input
                            type="range"
                            min={f.min}
                            max={f.max}
                            step={f.step}
                            value={values[f.key]}
                            disabled={idle}
                            onChange={(e) => setField(f, e.target.value)}
                            aria-label={`${g.name} ${f.label}`}
                          />
                          <input
                            className="pa-fit__num"
                            type="number"
                            min={f.min}
                            max={f.max}
                            step={f.step}
                            value={values[f.key]}
                            disabled={idle}
                            onChange={(e) => setField(f, e.target.value)}
                            aria-label={`${g.name} ${f.label} (${f.unit})`}
                          />
                          <span className="pa-layout__unit">{f.unit}</span>
                        </>
                      )}
                      <button
                        type="button"
                        className="btn-icon btn-icon--warning pa-layout__reset"
                        style={{ visibility: changed ? 'visible' : 'hidden' }}
                        onClick={() => setValues((prev) => ({ ...prev, [f.key]: f.default }))}
                        title={`Back to the design (${designValue(f)})`}
                        aria-label={`Reset ${g.name} ${f.label}`}
                      >
                        <RotateCcw size={14} aria-hidden="true" />
                      </button>
                    </div>
                    {/* The Music field's options carry a track to listen to: the chosen one's player. */}
                    {f.type === 'choice' && f.options.find((o) => o.value === values[f.key])?.audio && (
                      <audio
                        key={values[f.key]}
                        className="pa-layout__audio"
                        controls
                        preload="none"
                        src={f.options.find((o) => o.value === values[f.key]).audio}
                        aria-label={`${f.label}: ${f.options.find((o) => o.value === values[f.key]).label}`}
                      />
                    )}
                    {f.note && <div className="pa-layout__note">{f.note}</div>}
                  </Fragment>
                );
              })}
            </div>
          ))}
          <div className="pa-fit__actions">
            <button type="button" className="adm-act adm-act--green" disabled={saving || !dirty} onClick={save}>
              <Check size={13} aria-hidden="true" />
              {saving ? 'Saving…' : 'Save layout'}
            </button>
            {dirty && (
              <button type="button" className="adm-act adm-act--quiet" disabled={saving} onClick={() => setValues(fillLayout(fields, saved))}>
                Discard changes
              </button>
            )}
            {!atDesign && (
              <button type="button" className="adm-act adm-act--quiet" disabled={saving} onClick={() => setValues(fillLayout(fields, {}))}>
                Back to the design
              </button>
            )}
            <span className="pa-slot__hint">
              {dirty ? 'Unsaved changes — only the preview shows them' : 'Saved · overlays refresh within ~15 s'}
            </span>
          </div>
        </div>
      </div>
    </section>
  );
}

// The serve route 302s to the shipped file when no upload exists — and for
// slots with no shipped file either (most banners), that 302 lands on a 404.
// Swap the broken <img> for an honest empty state instead of a broken glyph.
function ArtPreview({ src, ratio, alt }) {
  const [failed, setFailed] = useState(false);
  useEffect(() => { setFailed(false); }, [src]);
  if (failed) {
    return (
      <div className="pa-preview pa-preview--empty" style={{ aspectRatio: ratio }}>
        No image yet
      </div>
    );
  }
  return (
    <img
      className="pa-preview"
      style={{ aspectRatio: ratio }}
      src={src}
      alt={alt}
      onError={() => setFailed(true)}
    />
  );
}
